import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Stack, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { calendarSourcesApi } from '../api/services';
import type { CalendarSource } from '../types/models';

export default function CalendarSourcesCard() {
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<CalendarSource> | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const all = await calendarSourcesApi.getAll();
      // Show only ICS sources here (OAuth sources shown in ConnectCalendarCard)
      setSources(all.filter(s => !s.sourceType || s.sourceType === 'ICS'));
    } catch (err) {
      console.error('Failed to load calendar sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleSave = async () => {
    if (!editItem?.name || !editItem?.icsUrl) return;
    try {
      if (editItem.id) {
        await calendarSourcesApi.update(editItem.id, editItem);
      } else {
        await calendarSourcesApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadSources();
    } catch (err) {
      console.error('Failed to save calendar source', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this calendar source?')) return;
    try {
      await calendarSourcesApi.delete(id);
      loadSources();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      await calendarSourcesApi.sync(id);
      loadSources();
    } catch (err) {
      console.error('Failed to sync', err);
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">ICS Calendar Sources</Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditItem({ name: '', icsUrl: '', isActive: true });
              setDialogOpen(true);
            }}
          >
            Add ICS Source
          </Button>
        </Box>
        {sources.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No ICS calendar sources configured</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ICS URL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Synced</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((source) => (
                  <TableRow key={source.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{source.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {source.icsUrl.length > 50 ? source.icsUrl.substring(0, 50) + '...' : source.icsUrl}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={source.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={source.isActive ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {source.lastSyncedAt
                          ? format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')
                          : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleSync(source.id)}
                        disabled={syncingId === source.id}
                        title="Sync"
                      >
                        <SyncIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setEditItem(source); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(source.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editItem?.id ? 'Edit Calendar Source' : 'Add Calendar Source'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                fullWidth
                value={editItem?.name || ''}
                onChange={e => setEditItem(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Work Calendar"
              />
              <TextField
                label="ICS URL"
                fullWidth
                value={editItem?.icsUrl || ''}
                onChange={e => setEditItem(prev => ({ ...prev, icsUrl: e.target.value }))}
                placeholder="https://example.com/calendar.ics"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editItem?.isActive ?? true}
                    onChange={e => setEditItem(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave}>Save</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
